import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans } from 'react-i18next';
import { IConfirmModalProps, IFavoriteProps, IFavouriteListModule } from '../../../interfaces/interfaces';
import keyboardTriggerBasic from '../../../utils/keyboard-trigger';
import randomId from '../../../utils/randomId';
import Link from '../Link/Link';
import { saveFavorites } from '../../../utils/data';
import UserTileContext from '../../../contexts/userTilesContext';

function FavouriteListModalEdit(props: IConfirmModalProps): JSX.Element {
  const modalRef = useRef<HTMLAnchorElement>(null);
  const { onAbort, list, onConfirm, isLocal } = props;
  const [listState, setListState] = useState(list as IFavoriteProps[]);
  const userTiles = useContext(UserTileContext);

  useEffect(() => {
    modalRef.current?.focus();
  }, []);

  const reset = () => {
    setListState(list as IFavoriteProps[]);
  };


  const onAbortClick = () => {
    reset();
    // close modal
    onAbort();
  };

  const saveEdit = () => {
    // make save request here
    const favoritesToSave = listState
      .filter((item) => !item.isDeleted)
      .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
      .map((item, index) => {
        return { ...item, sortIndex: index };
      });
    saveFavorites(favoritesToSave as IFavoriteProps[], isLocal)
      .then(() => {
        const [favTile] = userTiles.tiles.filter((tile) => tile.module === 'FavouriteListModule');
        if (favTile) {
          (favTile.props as IFavouriteListModule).favorites = favoritesToSave;
        }

        const newTiles = userTiles.tiles.map((tile) => {
          if (tile.id === favTile.id) {
            return favTile;
          }
          return tile;
        });

        userTiles.setTiles(newTiles);
      })
      .finally(onConfirm);
  };

  const deleteItem = (item: IFavoriteProps) => {
    const newList = listState?.map((link: IFavoriteProps) => {
      const newLink = { ...link };
      if (item.linkHref === link.linkHref) {
        newLink.isDeleted = true;
      }
      return newLink;
    });

    setListState(newList);
  };

  const itemUp = (item: IFavoriteProps) => {
    const oldList = listState?.sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)) || [];
    let indexBefore = -1;
    let newSortBefore = -1;
    // collect new sorted list (modifying "item" only)
    const newList = oldList.map((link: IFavoriteProps) => {
      if (item.linkHref === link.linkHref) {
        let newLink = { ...link };
        const ownIndex = oldList?.indexOf(item);
        if (ownIndex > -1) {
          newLink = { ...newLink, sortIndex: (link.sortIndex || 1) - 1 };
        }
        // store "item-before" & "item-before-sort" to modify later
        indexBefore = (ownIndex || 0) - 1;
        if (indexBefore > -1) {
          newSortBefore = (oldList[indexBefore].sortIndex || 0) + 1;
        }
        return newLink;
      }
      return link;
    });

    // modify "before-item" if necasary
    if (indexBefore >= 0 && newSortBefore >= 0) {
      newList[indexBefore].sortIndex = newSortBefore;
    }

    // update state
    setListState(newList);
  };

  const itemDown = (item: IFavoriteProps) => {
    const oldList = listState?.sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)) || [];
    let indexAfter = -1;
    let newSortAfter = -1;
    // collect new sorted list (modifying "item" only)
    const newList = oldList.map((link: IFavoriteProps) => {
      if (item.linkHref === link.linkHref) {
        let newLink = { ...link };
        const ownIndex = oldList?.indexOf(item);
        if (ownIndex > -1) {
          // store "item-after" & "item-after-sort" to modify later
          indexAfter = (ownIndex || 0) + 1;
          if (indexAfter > -1 && indexAfter < oldList.length) {
            newLink = { ...newLink, sortIndex: (link.sortIndex || 0) + 1 };
            newSortAfter = (oldList[indexAfter].sortIndex || 1) - 1;
          }
        }

        return newLink;
      }
      return link;
    });

    // modify "after-item" if necasary
    if (indexAfter >= 0 && newSortAfter >= 0) {
      newList[indexAfter].sortIndex = newSortAfter;
    }

    // update state
    setListState(newList);
  };

  const restore = (item: IFavoriteProps) => {
    const newList = listState?.map((link: IFavoriteProps) => {
      const newLink = { ...link };
      if (item.linkHref === link.linkHref) {
        newLink.isDeleted = false;
      }
      return newLink;
    });
    setListState(newList);
  };

  const tabIndexForState = (item: IFavoriteProps) => {
    return (item.isDeleted ? -1 : 0)
  };

  const tabIndexForArrowUp = (item: IFavoriteProps, index: number) => {
    return ((item.isDeleted || index === 0) ? -1 : 0)
  };

  const tabIndexForArrowDown = (item: IFavoriteProps, index: number) => {
    return ((item.isDeleted || index === listState.length-1) ? -1 : 0)
  };

  const modalContent = (
    <>
      <ul className="list edit-list">
        {listState
          ?.sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0))
          .map((item, index) => (
            <li key={randomId('favourite-list')}>
              <Link
                linkHref={item.linkHref}
                linkText={item.linkText}
                linkClass={`link ic-arrowRight ${item.linkClass} ${item.isDeleted ? 'disabled' : ''}`}
                tabIndex={tabIndexForState(item)}
              />
              {}
              <div className="edit-list-settings ">
                {!item.isDeleted ? (
                  <>
                    <a
                      className="ic-delete"
                      aria-label="entfernen"
                      tabIndex={tabIndexForState(item)}
                      role="button"
                      onClick={() => {
                        deleteItem(item);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<Element>) => {
                        keyboardTriggerBasic(e, () => {
                          deleteItem(item);
                        });
                      }}
                    >
                      <span className="u-hidden"><Trans i18nKey="cockpit.favourite.delete">entfernen</Trans></span>
                    </a>
                    <a
                      className="ic-arrowUp"
                      aria-label="nach oben verschieben"
                      tabIndex={tabIndexForArrowUp(item, index)}
                      role="button"
                      onClick={() => {
                        itemUp(item);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<Element>) => {
                        keyboardTriggerBasic(e, () => {
                          itemUp(item);
                        });
                      }}
                    >
                      <span className="u-hidden"><Trans i18nKey="cockpit.favourite.up">hoch</Trans></span>
                    </a>
                    <a
                      className="ic-arrowDown"
                      aria-label="nach unten verschieben"
                      tabIndex={tabIndexForArrowDown(item, index)}
                      role="button"
                      onClick={() => {
                        itemDown(item);
                      }}
                      onKeyDown={(e: React.KeyboardEvent<Element>) => {
                        keyboardTriggerBasic(e, () => {
                          itemDown(item);
                        });
                      }}
                    >
                      <span className="u-hidden"><Trans i18nKey="cockpit.favourite.down">runter</Trans></span>
                    </a>
                  </>
                ) : (
                  <a
                    className="ic-repeat"
                    aria-label="zurücksetzen"
                    tabIndex={0}
                    role="button"
                    onClick={() => {
                      restore(item);
                    }}
                    onKeyDown={(e: React.KeyboardEvent<Element>) => {
                      keyboardTriggerBasic(e, () => {
                        restore(item);
                      });
                    }}
                  >
                    <span className="u-hidden"><Trans i18nKey="cockpit.favourite.reset">zurücksetzen</Trans></span>
                  </a>
                )}
              </div>
            </li>
          ))}
      </ul>
      <div className="modal-actions">
        <a
          role="button"
          className="link--button"
          onClick={saveEdit}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, saveEdit);
          }}
          tabIndex={0}
        >
          <span className="ic-arrowRight" />
          <span className="link-text">
                  <Trans i18nKey="cockpit.favourite.save">Speichern</Trans>
                </span>
        </a>
        <a
          role="button"
          className="link--button link--button-secondary"
          onClick={onAbortClick}
          onKeyDown={(e) => {
            keyboardTriggerBasic(e, onAbortClick);
          }}
          tabIndex={0}
        >
          <span className="ic-close" />
          <span className="link-text">
                  <Trans i18nKey="cockpit.favourite.cancel">Abbrechen</Trans>
                </span>
        </a>
      </div>
    </>
  );

  return (
    <>
      <div className="modal favourite-list-modal-edit" role="dialog">
        <div className="modal-inner l-container">
          <a
            role="button"
            className="modal-closer"
            onClick={onAbortClick}
            onKeyDown={(e) => {
              keyboardTriggerBasic(e, onAbortClick);
            }}
            tabIndex={0}
            ref={modalRef}
          >
            <span className="ic-close" />
          </a>
          <div className="modal-inner-content">
            <h3 className="hl-3">
              <Trans i18nKey="cockpit.favourite.modalHeadline">Favoritenübersicht EDIT</Trans>
            </h3>
            <div className="scrollable-content">{modalContent}</div>

          </div>
        </div>
      </div>
    </>
  );
}
export default FavouriteListModalEdit;
